import { customEvents } from '../../config/custom-events';
import { config } from './config';
import { breakpoints } from '../../config/breakpoints';
import switchLinksTabIndex from '../../utils/switchLinksTabIndex';
import { showOverlay, hideOverlay } from '../../utils/pageOverlay';
import keyboardFocusCallback from '../../utils/keyboardFocusCallback';
import fadeNonActiveLinks from '../../utils/fadeNonActiveLinks';

/**
 * Destinations Nav separate logic
 */
export default function destinations() {
  /**
   * Destinations navigation wrapper
   * @type {Element|null}
   */
  const destinationsNavigation = document.querySelector('.js-destinations-navigation');

  /**
   * Destinations list navigation link selector (not inner links)
   * @type {string}
   */
  const destinationsListLinkSelector = '.js-destination-link';

  /**
   * Destinations list navigation hover link selector (not inner links)
   * @type {string}
   */
  const destinationsListHoverLinkSelector = '.js-destination-hover-link';

  /**
   * Destinations link selector
   * @type {string}
   */
  const linkSelector = '.js-destination-nav-link';

  /**
   * Destinations navigation toggles selector (keyboard navigation)
   * @type {string}
   */
  const keyboardToggleSelector = '.js-header-tab-destination-navigation-toggle';

  /**
   * Destination navigation toggle
   * @type {string}
   */
  const navigationToggle = document.querySelector('.js-destinations-navigation-toggle');

  /**
   * Highest level menu toggles (destinations list inside destination navigation)
   * @type {Element[]}
   */
  const toggles = document.querySelectorAll('.js-show-destination-navigation');

  /**
   * Destinations navigation related blocks selector
   * @type {string}
   */
  const navigationRelatedBlocks = document.querySelectorAll('.js-destinations-nav-related-block');

  /**
   * Destinations navigation wrapper selector
   * @type {string}
   */
  const destinationsListWrapper = '.js-navigation-destinations';

  /**
   * Destinations navigation faded modifier class
   * @type {string}
   */
  const linkStateFadedClass = 'navigation__destination-link--state-faded';

  /**
   * Common callback for navigation keyboard navigation
   * @type {function(): void}
   */
  const returnToToggleTabulationCallback = () => {
    const btn = navigationToggle?.querySelector(keyboardToggleSelector);

    if (btn) {
      setTimeout(() => {
        btn.focus();
      }, 1);
    } else {
      document.dispatchEvent(new CustomEvent(customEvents.navigation.destinations.close));
    }
  };

  /**
   * Opacity effect for destinations links
   */
  [linkSelector, destinationsListHoverLinkSelector].forEach((selector) => {
    fadeNonActiveLinks(selector, linkStateFadedClass);
  });

  /**
   * Function for proper keyboard navigation on destinations list (1st level)
   */
  function handleKeyboardFocusForDestinationsList() {
    // Destinations list navigation focus handler
    // eslint-disable-next-line max-len
    const destinationsListWrap = destinationsNavigation.querySelector(destinationsListWrapper);
    // eslint-disable-next-line max-len
    const destinationsListLinks = destinationsListWrap.querySelectorAll(destinationsListLinkSelector);
    const checkForActiveDestinationMenuAndReturnToToggleIfNoItems = () => {
      const activeDestinationMenu = Array.from(navigationRelatedBlocks).filter((el) => el.dataset.active === 'true');
      // close destinations navigation if there is no active destination menu
      if (activeDestinationMenu.length === 0) {
        return returnToToggleTabulationCallback();
      }
      return true;
    };

    keyboardFocusCallback(
      destinationsListWrap,
      checkForActiveDestinationMenuAndReturnToToggleIfNoItems,
      returnToToggleTabulationCallback,
      destinationsListLinks,
    );
  }

  /**
   * Function for proper keyboard navigation on inner menus (2nd level)
   */
  function handleKeyboardFocusForInnerMenus() {
    // Combine all inner menus by data-navigation attribute
    // eslint-disable-next-line max-len
    const navRelatedBlocksCombinedByDataNavigation = Array.from(navigationRelatedBlocks).reduce((acc, el) => {
      const { navigation } = el.dataset;
      if (!acc[navigation]) {
        acc[navigation] = [];
      }
      acc[navigation].push(el);
      return acc;
    }, []);

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const menu in navRelatedBlocksCombinedByDataNavigation) {
      let elements = [];
      navRelatedBlocksCombinedByDataNavigation[menu].forEach((el) => {
        const links = el.querySelectorAll('a[href], button, input, textarea, select, details');
        elements = [...elements, ...links];
      });

      keyboardFocusCallback(
        destinationsNavigation,
        returnToToggleTabulationCallback,
        returnToToggleTabulationCallback,
        elements,
      );
    }
  }

  toggles.forEach((toggle) => {
    ['mouseover', 'click'].forEach((event) => {
      toggle.addEventListener(event, () => {
        const { navigationMenu, href } = toggle.dataset;

        document.querySelectorAll(destinationsListLinkSelector).forEach((el) => {
          const isEqual = el.isEqualNode(toggle);
          el.setAttribute('data-active', isEqual.toString());
        });

        // set main image as background for placeholder
        if (toggle.dataset.imageUrl) {
          destinationsNavigation.querySelector(config.imageHoverPlaceholderSelector).style.backgroundImage = `url(${toggle.dataset.imageUrl})`;
        }

        if (window.screen.width > breakpoints.md) {
          navigationRelatedBlocks.forEach((block) => {
            const el = block;
            const { navigation, activeModifier } = block.dataset;

            if (navigation === navigationMenu) {
              switchLinksTabIndex(el, '0', 100);
              el.classList.add(activeModifier);
              el.setAttribute('data-active', 'true');
            } else {
              switchLinksTabIndex(el, '-1', 100);
              el.classList.remove(activeModifier);
              el.setAttribute('data-active', 'false');
            }
          });
        } else {
          window.location.href = href;
        }
      });
    });
  });

  /**
   * Events handlers
   */
  document.addEventListener(customEvents.navigation.destinations.open, () => {
    const primaryHeaderLevelNav = document.querySelector(config.headerPrimaryLevelSelector);
    const secondaryHeaderLevelNav = document.querySelector(config.headerSecondaryLevelSelector);

    if (primaryHeaderLevelNav) {
      primaryHeaderLevelNav.classList.add(config.headerLevelFadeClass);
    }

    if (secondaryHeaderLevelNav) {
      secondaryHeaderLevelNav.classList.add(config.headerLevelHideClass);
    }

    // close main navigation
    document.dispatchEvent(new Event(customEvents.navigation.main.close));
    navigationToggle.setAttribute('aria-expanded', 'true');
    destinationsNavigation.classList.add(config.navigationOpenModifierClass);
    navigationToggle.querySelector(keyboardToggleSelector).setAttribute('data-active', 'true');

    if (window.screen.width > breakpoints.md) {
      import('../../utils/changeImageOnLinkFocus').then((module) => {
        module.default(
          linkSelector,
          destinationsNavigation.querySelector(config.imageHoverPlaceholderSelector),
          null,
        );
      });
    }

    showOverlay();
    switchLinksTabIndex(destinationsNavigation.querySelector(destinationsListWrapper), '0');
    document.querySelector(config.body).classList.add(config.bodyLockScrollClass);
  });

  document.addEventListener(customEvents.navigation.destinations.close, () => {
    const primaryHeaderLevelNav = document.querySelector(config.headerPrimaryLevelSelector);
    const secondaryHeaderLevelNav = document.querySelector(config.headerSecondaryLevelSelector);
    const image = destinationsNavigation.querySelector(config.imageHoverPlaceholderSelector);

    if (secondaryHeaderLevelNav) {
      secondaryHeaderLevelNav.classList.remove(config.headerLevelHideClass);
    }

    if (primaryHeaderLevelNav) {
      primaryHeaderLevelNav.classList.remove(config.headerLevelFadeClass);
    }

    if (image) {
      image.style.backgroundImage = '';
    }

    navigationToggle.setAttribute('aria-expanded', 'false');
    destinationsNavigation.classList.remove(config.navigationOpenModifierClass);
    navigationToggle.querySelector(keyboardToggleSelector).setAttribute('data-active', 'false');

    hideOverlay();

    navigationRelatedBlocks.forEach((block) => {
      const el = block;
      const { activeModifier } = block.dataset;

      el.classList.remove(activeModifier);
      el.setAttribute('data-active', 'false');
    });

    switchLinksTabIndex(destinationsNavigation, '-1');

    document.querySelectorAll(destinationsListHoverLinkSelector).forEach((el) => { el.classList.remove(linkStateFadedClass); el.setAttribute('data-active', 'init'); });
    document.querySelector(config.body).classList.remove(config.bodyLockScrollClass);
  });

  document.addEventListener('DOMContentLoaded', () => {
    handleKeyboardFocusForDestinationsList();
    handleKeyboardFocusForInnerMenus();
  });
}
